import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { markSlideViewed, getActivityProgress } from '../utils/progressTracker';

const ACTIVITY_ID = 'bac-assp-bloc3';
const theme = { primary: '#D97706', secondary: '#F59E0B', bg: '#FFFBEB', text: '#1a2e35' };
const titleImage = "https://static.prod-images.emergentagent.com/jobs/5ffa2715-dbd5-4a4a-b5be-ca1657b7c30e/images/7fa0e1bd9fd9a2e7a579f0115e4ae1cb78282d0aa6ae4a7150e05f3243b0ffc8.png";
const bgImage = "https://static.prod-images.emergentagent.com/jobs/5ffa2715-dbd5-4a4a-b5be-ca1657b7c30e/images/7fa0e1bd9fd9a2e7a579f0115e4ae1cb78282d0aa6ae4a7150e05f3243b0ffc8.png";

const slides = [
  // ===== TITRE =====
  { type: 'title', image: titleImage, blocTitle: "BLOC 3 – TRAVAILLER ET COMMUNIQUER AU SEIN D'UNE ÉQUIPE PLURIPROFESSIONNELLE" },

  // ===== SOMMAIRE =====
  { type: 'sommaire', sections: [
    { num: "1", title: "Le travail en équipe pluriprofessionnelle", target: 6 },
    { num: "2", title: "La communication professionnelle en équipe", target: 21 },
    { num: "3", title: "Les transmissions professionnelles", target: 37 },
    { num: "4", title: "Les outils numériques et documents professionnels", target: 54 },
    { num: "5", title: "Participation à la démarche qualité", target: 65 },
    { num: "6", title: "Prévention des risques professionnels", target: 80 },
    { num: "7", title: "Accueil, tutorat et accompagnement", target: 95 },
    { num: "8", title: "Attendus professionnels et VAE", target: 107 },
  ]},

  // ===== INTRODUCTION =====
  { type: 'subsection', title: "Introduction" },
  { type: 'richtext', title: "Travailler et communiquer en équipe", blocks: [
    { text: "Dans le secteur sanitaire et social, la qualité de l'accompagnement repose sur le travail en équipe. Aucun professionnel n'agit seul. Chaque intervention s'inscrit dans une dynamique collective où les informations doivent circuler, les rôles être compris et les actions être coordonnées." },
  ]},
  { type: 'richtext', title: "Compétences attendues", blocks: [
    { text: "Le professionnel du Bac Pro ASSP doit être capable de :", list: [
      "Communiquer avec différents interlocuteurs",
      "Transmettre des informations utiles",
      "Participer à la continuité de l'accompagnement",
      "S'inscrire dans une démarche de qualité et de prévention des risques",
    ]},
    { text: "Le travail en équipe pluriprofessionnelle suppose de connaître sa place, de respecter celle des autres, de comprendre les limites de son champ d'intervention et d'adopter une posture professionnelle adaptée." },
  ]},

  // =============================================
  // CHAPITRE 1 : Le travail en équipe pluriprofessionnelle
  // =============================================
  { type: 'section', section: "1", title: "Le travail en équipe pluriprofessionnelle" },

  // --- 1.1 Définition et enjeux ---
  { type: 'subsection', title: "1.1 Définition et enjeux du travail en équipe" },
  { type: 'richtext', title: "Définition du travail en équipe", blocks: [
    { text: "Le travail en équipe pluriprofessionnelle désigne la collaboration entre plusieurs professionnels exerçant des fonctions complémentaires auprès d'une même personne ou d'un même groupe." },
    { text: "Cette coopération permet d'assurer une prise en charge globale, cohérente et continue." },
  ]},
  { type: 'richtext', title: "Des besoins multiples", blocks: [
    { text: "Dans le secteur sanitaire et social, les besoins des personnes accompagnées sont souvent multiples. Ils concernent à la fois :", list: [
      "L'hygiène et la santé",
      "La sécurité",
      "La relation et le soutien psychologique",
      "L'autonomie",
      "La vie sociale",
    ]},
    { text: "Aucun professionnel ne peut répondre seul à l'ensemble de ces besoins. Le travail en équipe permet donc de croiser les regards, de partager les informations pertinentes et de mieux ajuster les réponses apportées." },
  ]},

  // --- 1.2 Les différents professionnels ---
  { type: 'subsection', title: "1.2 Les différents professionnels du secteur" },
  { type: 'richtext', title: "Composition de l'équipe", blocks: [
    { text: "Selon les structures, l'équipe peut être composée de :", list: [
      "Aides-soignants",
      "Auxiliaires de puériculture",
      "Infirmiers (IDE)",
      "Médecins",
      "Psychologues",
      "Éducateurs spécialisés",
      "Ergothérapeutes",
      "Psychomotriciens",
    ]},
  ]},
  { type: 'richtext', title: "Autres membres de l'équipe", blocks: [
    { text: "L'équipe peut également comprendre :", list: [
      "Animateurs",
      "Agents de service hospitalier (ASH)",
      "Assistants sociaux",
      "Cadres de santé",
      "Responsables de service",
    ]},
    { text: "Chaque professionnel intervient dans le cadre de ses missions, de ses compétences et de sa qualification. L'accompagnement de qualité repose sur la reconnaissance de cette complémentarité." },
  ]},

  // --- 1.3 Rôles, fonctions, complémentarité ---
  { type: 'subsection', title: "1.3 Rôles, complémentarité et limites" },
  { type: 'richtext', title: "Connaître son rôle et ses limites", blocks: [
    { text: "Le travail en équipe suppose que chacun connaisse son rôle et les limites de son intervention. Le professionnel ASSP doit savoir :", list: [
      "Ce qu'il peut faire seul",
      "Ce qu'il doit faire en collaboration",
      "Ce qui relève d'un autre professionnel",
    ]},
  ]},
  { type: 'richtext', title: "Garantir la sécurité", blocks: [
    { text: "Cette connaissance des limites de compétences est essentielle pour garantir la sécurité de la personne accompagnée et la qualité de l'intervention." },
    { text: "Elle évite :", list: [
      "Les erreurs",
      "Les confusions de rôle",
      "Les prises d'initiative inadaptées",
    ]},
  ]},

  // --- 1.4 Place du professionnel ASSP ---
  { type: 'subsection', title: "1.4 Place du professionnel ASSP dans l'équipe" },
  { type: 'richtext', title: "Un regard précieux", blocks: [
    { text: "Le professionnel du Bac Pro ASSP occupe une place importante dans l'équipe, car il est souvent au plus près de la personne dans le quotidien. Il observe, accompagne, aide, stimule, sécurise et transmet." },
    { text: "Son regard est précieux, car il est en contact direct avec les habitudes, les réactions, les besoins et les évolutions de la personne accompagnée." },
    { text: "Il participe à la continuité de l'accompagnement en apportant des informations concrètes issues du terrain." },
  ]},

  // --- 1.5 Organisation du travail ---
  { type: 'subsection', title: "1.5 Organisation du travail et coordination" },
  { type: 'richtext', title: "Organisation et coordination", blocks: [
    { text: "L'organisation du travail repose sur :", list: [
      "La coordination des actions",
      "Le respect des horaires",
      "La répartition des tâches",
      "La hiérarchisation des priorités",
      "La capacité à s'adapter aux imprévus",
    ]},
    { text: "Le professionnel doit tenir compte du fonctionnement du service, des contraintes de l'équipe et des besoins des personnes accompagnées." },
  ]},
  { type: 'richtext', title: "Éviter les dysfonctionnements", blocks: [
    { text: "La coordination permet d'éviter :", list: [
      "Les oublis dans l'accompagnement",
      "Les doublons (deux professionnels réalisant la même action sans le savoir)",
      "Les contradictions dans les interventions",
    ]},
  ]},

  // =============================================
  // CHAPITRE 2 : La communication professionnelle
  // =============================================
  { type: 'section', section: "2", title: "La communication professionnelle en équipe" },

  // --- 2.1 Enjeux ---
  { type: 'subsection', title: "2.1 Enjeux de la communication professionnelle" },
  { type: 'richtext', title: "Continuité, cohérence et sécurité", blocks: [
    { text: "La communication professionnelle permet d'assurer la continuité, la cohérence et la sécurité de l'accompagnement." },
    { text: "Une information mal transmise, incomplète ou imprécise peut avoir des conséquences importantes pour la personne accompagnée." },
  ]},
  { type: 'richtext', title: "Communiquer, ce n'est pas seulement parler", blocks: [
    { text: "Communiquer en équipe, ce n'est pas seulement parler. C'est transmettre :", list: [
      "Les bonnes informations",
      "Au bon moment",
      "À la bonne personne",
      "Avec un langage clair, professionnel et adapté",
    ]},
  ]},

  // --- 2.2 Communication orale ---
  { type: 'subsection', title: "2.2 Communication orale au quotidien" },
  { type: 'richtext', title: "La communication orale", blocks: [
    { text: "La communication orale se déroule au quotidien dans les échanges entre collègues, lors des relèves, des réunions, des demandes d'aide ou des ajustements de planning." },
    { text: "Elle doit être :", list: [
      "Claire et concise",
      "Respectueuse",
      "Centrée sur les faits utiles à l'accompagnement",
    ]},
  ]},
  { type: 'richtext', title: "Posture professionnelle dans l'échange", blocks: [
    { text: "Le professionnel doit :", list: [
      "Éviter les jugements de valeur",
      "Éviter les interprétations non vérifiées",
      "Éviter les propos inadaptés",
      "Savoir écouter les autres membres de l'équipe",
      "Reformuler si nécessaire pour s'assurer de la bonne compréhension",
    ]},
  ]},

  // --- 2.3 Communication écrite ---
  { type: 'subsection', title: "2.3 Communication écrite professionnelle" },
  { type: 'richtext', title: "Les formes de l'écrit professionnel", blocks: [
    { text: "La communication écrite prend différentes formes :", list: [
      "Transmissions ciblées dans le dossier de soins",
      "Fiches de suivi et observations",
      "Tableaux de bord (alimentation, hydratation, constantes)",
      "Comptes-rendus de réunion",
      "Documents de liaison entre services ou établissements",
    ]},
  ]},
  { type: 'richtext', title: "Règles de l'écrit professionnel", blocks: [
    { text: "La communication écrite doit être lisible, datée, objective, structurée et utile à l'équipe." },
    { text: "Le professionnel écrit ce qu'il observe, ce qu'il fait et ce qu'il constate, sans formuler d'opinion personnelle inutile." },
    { note: "Tout ce qui n'est pas écrit est considéré comme non fait. La traçabilité écrite est une obligation professionnelle et un gage de sécurité." },
  ]},

  // --- 2.4 Communication formelle et informelle ---
  { type: 'subsection', title: "2.4 Communication formelle et informelle" },
  { type: 'richtext', title: "Formelle et informelle", blocks: [
    { text: "La communication formelle correspond aux temps et supports prévus par l'organisation :", list: [
      "Réunions d'équipe, synthèses, relèves",
      "Transmissions écrites dans le dossier",
      "Logiciels professionnels",
    ]},
    { text: "La communication informelle regroupe les échanges plus spontanés du quotidien entre collègues." },
    { note: "Les deux sont importantes, mais les informations essentielles doivent toujours être tracées selon les procédures de la structure." },
  ]},

  // --- 2.5 Transmission d'informations ---
  { type: 'subsection', title: "2.5 Transmission d'informations utiles" },
  { type: 'richtext', title: "Sélectionner ce qui est utile", blocks: [
    { text: "Toutes les informations ne se valent pas. Le professionnel doit apprendre à sélectionner ce qui est utile à transmettre :", list: [
      "Modification du comportement ou changement d'humeur",
      "Douleur exprimée ou observée",
      "Chute ou quasi-chute",
      "Refus alimentaire ou difficulté d'alimentation",
      "Difficulté d'élimination",
      "Évolution de l'autonomie",
      "Réaction à une activité",
      "État cutané (rougeur, lésion, escarres)",
      "Événement inhabituel",
    ]},
    { text: "Les informations transmises doivent être fiables, observables et pertinentes." },
  ]},

  // --- 2.6 Confidentialité ---
  { type: 'subsection', title: "2.6 Confidentialité et secret professionnel" },
  { type: 'richtext', title: "Secret professionnel et discrétion", blocks: [
    { text: "Le travail en équipe ne dispense pas du respect du secret professionnel. Les informations ne doivent être partagées qu'avec les professionnels concernés par l'accompagnement et dans le strict cadre de leurs missions." },
    { text: "Le professionnel doit :", list: [
      "Faire preuve de discrétion en toutes circonstances",
      "Éviter les conversations inappropriées sur les personnes accompagnées",
      "Ne jamais parler d'un patient dans un lieu public (couloir, ascenseur, cafétéria)",
      "Ne pas divulguer d'informations aux familles sans accord",
      "Respecter la confidentialité des données",
      "Verrouiller sa session informatique en quittant son poste",
    ]},
    { note: "La violation du secret professionnel est passible de sanctions disciplinaires et pénales (article 226-13 du Code pénal)." },
  ]},

  // =============================================
  // CHAPITRE 3 : Les transmissions professionnelles
  // =============================================
  { type: 'section', section: "3", title: "Les transmissions professionnelles" },

  // --- 3.1 Définition et finalités ---
  { type: 'subsection', title: "3.1 Définition et finalités des transmissions" },
  { type: 'richtext', title: "Définition des transmissions", blocks: [
    { text: "Les transmissions ont pour objectif d'assurer la continuité des soins et de l'accompagnement. Elles permettent à chaque membre de l'équipe de disposer des informations nécessaires pour adapter son intervention." },
    { text: "Les transmissions évitent les ruptures dans la prise en charge et favorisent une approche cohérente." },
  ]},
  { type: 'richtext', title: "Finalités des transmissions", blocks: [
    { text: "Les transmissions permettent de :", list: [
      "Assurer le suivi de l'état de santé et du bien-être de la personne",
      "Informer l'équipe des événements survenus pendant le poste",
      "Transmettre les observations, les soins réalisés et les résultats obtenus",
      "Garantir la continuité entre les équipes de jour, de nuit et du week-end",
    ]},
  ]},

  // --- 3.2 Transmissions orales ---
  { type: 'subsection', title: "3.2 Transmissions orales" },
  { type: 'richtext', title: "Les transmissions orales", blocks: [
    { text: "Les transmissions orales ont lieu notamment lors des relèves. Elles permettent de faire circuler rapidement les informations importantes." },
    { text: "Elles doivent être :", list: [
      "Structurées : suivre un ordre logique (par chambre, par secteur)",
      "Centrées sur les éléments significatifs",
      "Hiérarchisées : signaler en premier ce qui relève d'une vigilance particulière ou d'une urgence",
    ]},
    { note: "Les transmissions orales complètent les transmissions écrites mais ne les remplacent pas." },
  ]},

  // --- 3.3 Transmissions écrites ---
  { type: 'subsection', title: "3.3 Transmissions écrites" },
  { type: 'richtext', title: "Les transmissions écrites", blocks: [
    { text: "Les transmissions écrites gardent une trace des observations et des actions. Elles sont indispensables dans une logique de suivi, de coordination et de responsabilité professionnelle." },
    { text: "Elles doivent être rédigées avec précision, dans un vocabulaire professionnel, sans jugement ni approximation." },
  ]},
  { type: 'richtext', title: "Supports des transmissions écrites", blocks: [
    { text: "Les transmissions écrites s'inscrivent sur différents supports :", list: [
      "Dossier de soins informatisé",
      "Transmissions ciblées (méthode DAR)",
      "Feuilles de surveillance (constantes, hydratation, alimentation, selles, poids)",
      "Fiches de liaison (transfert entre services, rendez-vous externes)",
    ]},
  ]},

  // --- 3.4 Transmissions ciblées et méthode DAR ---
  { type: 'subsection', title: "3.4 Transmissions ciblées — Méthode DAR" },
  { type: 'richtext', title: "La méthode DAR", blocks: [
    { text: "Dans certaines structures, les transmissions ciblées sont utilisées. Elles permettent de présenter les informations de manière structurée. La méthode DAR signifie :", list: [
      "D — Données : ce qui est observé ou exprimé par la personne",
      "A — Actions : ce qui a été mis en place par le professionnel",
      "R — Résultats : les effets observés après l'intervention",
    ]},
    { text: "Cette méthode favorise la clarté, la lisibilité et l'efficacité des transmissions." },
  ]},
  { type: 'richtext', title: "Exemple de transmission DAR", blocks: [
    { note: "Cible : Douleur. D : Mme X se plaint de douleurs lombaires, évaluée à 6/10 sur l'EN. Faciès crispé, position antalgique au lit. A : Installation en position antalgique avec coussin, prévenu l'IDE qui a administré un antalgique prescrit. R : Douleur diminuée à 3/10 après 30 minutes, Mme X est détendue, a pu se rendormir." },
  ]},

  // --- 3.5 Traçabilité ---
  { type: 'subsection', title: "3.5 Traçabilité des actes et observations" },
  { type: 'richtext', title: "Traçabilité", blocks: [
    { text: "La traçabilité consiste à garder la preuve des actes réalisés, des observations faites et des informations transmises." },
    { text: "Elle permet de :", list: [
      "Sécuriser l'accompagnement",
      "Faciliter le suivi de la personne dans le temps",
      "Contribuer à la qualité du travail en équipe",
      "Prouver que le soin a été réalisé (protection juridique)",
    ]},
    { note: "Ce qui n'est pas écrit n'existe pas aux yeux de la loi. La traçabilité est une obligation professionnelle." },
  ]},

  // --- 3.6 Qualité des transmissions ---
  { type: 'subsection', title: "3.6 Qualité des transmissions" },
  { type: 'richtext', title: "Transmissions de qualité", blocks: [
    { text: "Des transmissions de qualité sont :", list: [
      "Factuelles : basées sur des faits observés",
      "Utiles : apportant un élément nécessaire à la prise en charge",
      "Précises : utilisant un vocabulaire professionnel adapté",
      "Compréhensibles : lisibles par l'ensemble de l'équipe",
    ]},
  ]},
  { type: 'richtext', title: "Erreurs à éviter", blocks: [
    { text: "Le professionnel doit éviter :", list: [
      "Les formulations vagues (« RAS » sans détail, « ça va »)",
      "Les jugements de valeur (« elle est difficile », « il est pénible »)",
      "Les suppositions et interprétations non vérifiées",
      "Les oublis de date ou de signature",
      "Les informations inutiles ou hors sujet",
      "Les abréviations non standardisées",
    ]},
    { text: "Il doit également veiller à la lisibilité et au respect des habitudes du service." },
  ]},

  // =============================================
  // CHAPITRE 4 : Les outils numériques et documents
  // =============================================
  { type: 'section', section: "4", title: "Les outils numériques et documents professionnels" },

  // --- 4.1 Dossier de la personne ---
  { type: 'subsection', title: "4.1 Dossier de la personne accompagnée" },
  { type: 'richtext', title: "Le dossier de la personne", blocks: [
    { text: "Le dossier de la personne regroupe les informations nécessaires à la continuité de l'accompagnement." },
    { text: "Il peut contenir :", list: [
      "Des éléments administratifs et identitaires",
      "Des observations et des transmissions",
      "Des habitudes de vie",
      "Des plannings de soins",
      "Des projets personnalisés d'accompagnement",
      "Des prescriptions médicales et feuilles de surveillance",
    ]},
    { text: "Le professionnel doit savoir le consulter, l'utiliser et y consigner certaines informations selon sa fonction." },
  ]},

  // --- 4.2 Logiciels professionnels ---
  { type: 'subsection', title: "4.2 Logiciels professionnels et outils numériques" },
  { type: 'richtext', title: "Logiciels métiers", blocks: [
    { text: "De nombreuses structures utilisent des logiciels métiers permettant de centraliser les informations :", list: [
      "Dossier de soins informatisé (DSI) : centralise toutes les informations du patient",
      "Logiciels métiers : NetSoins, Titan, Osiris, Livia, etc.",
      "Planning de soins informatisé : organisation des tâches par patient",
      "Messagerie sécurisée : communication entre professionnels (MSSanté)",
    ]},
    { text: "Le professionnel doit apprendre à s'y repérer, à renseigner les données autorisées et à utiliser ces outils avec rigueur." },
    { note: "Chaque acte doit être tracé informatiquement. Le professionnel est responsable de ce qu'il enregistre sous son identifiant." },
  ]},

  // --- 4.3 Planning, tableaux, fiches ---
  { type: 'subsection', title: "4.3 Planning, tableaux de suivi, fiches de liaison" },
  { type: 'richtext', title: "Outils de suivi au quotidien", blocks: [
    { text: "Le travail en équipe s'appuie aussi sur des outils plus simples :", list: [
      "Plannings : répartition des tâches sur la journée, par personne",
      "Tableaux de suivi : hydratation, alimentation, selles, poids, comportement",
      "Fiches de liaison : document accompagnant la personne lors d'un transfert",
      "Feuilles de présence",
    ]},
    { text: "Ces documents permettent d'organiser les actions, de visualiser les priorités et de partager certaines informations utiles." },
  ]},

  // --- 4.4 Utilisation responsable ---
  { type: 'subsection', title: "4.4 Utilisation responsable du numérique" },
  { type: 'richtext', title: "Usage strictement professionnel", blocks: [
    { text: "L'usage du numérique doit rester strictement professionnel. Le professionnel doit :", list: [
      "Respecter les règles de sécurité de l'établissement",
      "Ne pas laisser un poste ouvert sans surveillance",
      "Utiliser ses identifiants personnels (NE JAMAIS partager)",
      "Verrouiller sa session en quittant le poste",
      "Éviter toute consultation ou diffusion non autorisée d'informations",
      "Ne pas stocker de données de santé sur des supports personnels (clé USB, téléphone)",
      "Ne pas photographier des patients ou des documents avec son téléphone personnel",
    ]},
  ]},

  // --- 4.5 Protection des données et RGPD ---
  { type: 'subsection', title: "4.5 Protection des données et RGPD" },
  { type: 'richtext', title: "RGPD et données de santé", blocks: [
    { text: "Les données de santé et les données personnelles sont sensibles. Elles sont protégées par le RGPD (Règlement Général sur la Protection des Données, 2018)." },
    { text: "Le professionnel doit :", list: [
      "Respecter les règles de confidentialité",
      "Ne consulter que les informations nécessaires à sa mission",
      "Ne jamais utiliser ou diffuser les données en dehors du cadre prévu",
      "Verrouiller automatiquement le poste en cas d'absence",
      "Ne pas laisser de feuilles imprimées visibles, utiliser le destructeur",
    ]},
    { note: "Sanction en cas de violation : disciplinaire et pénale. Le non-respect du RGPD peut entraîner des amendes très lourdes pour l'établissement." },
  ]},

  // =============================================
  // CHAPITRE 5 : Participation à la démarche qualité
  // =============================================
  { type: 'section', section: "5", title: "Participation à la démarche qualité" },

  // --- 5.1 Notion de qualité ---
  { type: 'subsection', title: "5.1 Notion de qualité dans le secteur" },
  { type: 'richtext', title: "La qualité dans le secteur sanitaire et social", blocks: [
    { text: "La qualité désigne l'ensemble des moyens mis en œuvre pour garantir un accompagnement sécurisé, respectueux, efficace et conforme aux besoins des personnes." },
    { text: "Elle concerne à la fois :", list: [
      "L'organisation du travail et des services",
      "Les pratiques professionnelles",
      "Les outils utilisés au quotidien",
      "L'évaluation régulière des pratiques",
      "La prise en compte de la satisfaction de la personne accompagnée",
      "L'amélioration continue des organisations et des soins",
    ]},
  ]},

  // --- 5.2 Protocoles, procédures ---
  { type: 'subsection', title: "5.2 Protocoles, procédures et recommandations" },
  { type: 'richtext', title: "Protocoles et procédures", blocks: [
    { text: "Les protocoles et les procédures permettent d'harmoniser les pratiques, de sécuriser les actions et de réduire les risques.", list: [
      "Protocole : décrit les étapes d'un soin ou d'une action (ex : protocole de bionettoyage)",
      "Procédure : décrit l'organisation d'un processus (ex : procédure d'accueil d'un nouveau résident)",
      "Recommandations : guides de bonnes pratiques émis par les autorités (HAS, ARS)",
    ]},
    { text: "Le professionnel doit les connaître, les appliquer et signaler les difficultés éventuelles rencontrées dans leur mise en œuvre." },
  ]},

  // --- 5.3 Repérage des dysfonctionnements ---
  { type: 'subsection', title: "5.3 Repérage des dysfonctionnements" },
  { type: 'richtext', title: "Repérer les dysfonctionnements", blocks: [
    { text: "Dans son activité quotidienne, le professionnel peut repérer des anomalies, des oublis, des erreurs, des situations dangereuses ou des dysfonctionnements organisationnels." },
    { text: "Exemples :", list: [
      "Un matériel défectueux ou absent",
      "Un protocole non respecté",
      "Un risque identifié mais non traité",
      "Un manque de coordination entre professionnels",
      "Une insatisfaction exprimée par la personne accompagnée ou sa famille",
    ]},
    { text: "Il doit être capable de les identifier et de les signaler selon les procédures en vigueur." },
    { note: "Repérer un dysfonctionnement n'est pas dénoncer. C'est participer à l'amélioration de la qualité et à la sécurité de tous." },
  ]},

  // --- 5.4 Signalement des événements indésirables ---
  { type: 'subsection', title: "5.4 Signalement des événements indésirables" },
  { type: 'richtext', title: "Événement indésirable (EI)", blocks: [
    { text: "Un événement indésirable est une situation inattendue pouvant avoir ou ayant eu un effet négatif sur la personne accompagnée, un professionnel ou l'organisation." },
    { text: "Exemples d'EI :", list: [
      "Chute d'un résident",
      "Erreur de médicament",
      "Fausse route pendant un repas",
      "Escarres non repérées",
      "Fugue ou disparition",
    ]},
    { text: "Le signalement permet de comprendre ce qui s'est passé et de prévenir la répétition du problème." },
  ]},
  { type: 'richtext', title: "La fiche d'événement indésirable", blocks: [
    { text: "Tout EI doit faire l'objet d'une déclaration sur une fiche spécifique qui décrit :", list: [
      "Qui est concerné (patient, professionnel)",
      "Quoi : description précise de l'événement",
      "Quand : date et heure",
      "Où : lieu de l'événement",
      "Comment : circonstances, causes identifiées",
      "Conséquences et mesures prises",
    ]},
    { note: "Déclarer n'est PAS dénoncer — c'est améliorer la sécurité pour tous. La culture de la déclaration est un pilier de la démarche qualité." },
  ]},

  // --- 5.5 Actions correctives ---
  { type: 'subsection', title: "5.5 Mise en œuvre d'actions correctives" },
  { type: 'richtext', title: "Analyser et corriger", blocks: [
    { text: "La démarche qualité ne s'arrête pas au constat. Elle suppose de réfléchir à des actions correctives ou préventives." },
    { text: "Méthodes d'analyse :", list: [
      "Méthode des 5 pourquoi : remonter à la cause racine",
      "Arbre des causes : identifier l'enchaînement des facteurs",
      "Réunion CREX (Comité de Retour d'Expérience) : analyse collective",
    ]},
    { text: "Le professionnel peut y contribuer par ses observations, ses propositions et sa participation aux échanges d'équipe." },
  ]},

  // --- 5.6 Culture de sécurité ---
  { type: 'subsection', title: "5.6 Culture de sécurité et amélioration continue" },
  { type: 'richtext', title: "Culture de sécurité", blocks: [
    { text: "La culture de sécurité consiste à considérer que chacun a un rôle dans la prévention des risques et l'amélioration des pratiques." },
    { text: "Le professionnel participe à cette dynamique en :", list: [
      "Restant vigilant dans sa pratique quotidienne",
      "Respectant les protocoles et les procédures",
      "Transmettant les informations utiles à l'équipe",
      "Signalant les dysfonctionnements et les événements indésirables",
    ]},
  ]},
  { type: 'richtext', title: "Certification HAS", blocks: [
    { text: "La certification HAS (Haute Autorité de Santé) est une procédure d'évaluation externe des établissements visant à améliorer la qualité et la sécurité des soins :", list: [
      "EPP (Évaluation des Pratiques Professionnelles) : analyse par rapport aux recommandations",
      "Indicateurs qualité : taux de chutes, escarres, infections nosocomiales, satisfaction",
      "Audits internes : vérification du respect des protocoles",
      "Plan d'amélioration continue : identifier les écarts, corriger, évaluer",
    ]},
  ]},

  // =============================================
  // CHAPITRE 6 : Prévention des risques professionnels
  // =============================================
  { type: 'section', section: "6", title: "Prévention des risques professionnels" },

  // --- 6.1 Identification des risques ---
  { type: 'subsection', title: "6.1 Identification des risques professionnels" },
  { type: 'richtext', title: "Les risques professionnels", blocks: [
    { text: "Le secteur sanitaire et social expose les professionnels à de nombreux risques. L'identification de ces risques est la première étape de la prévention." },
    { text: "Les principaux types de risques :", list: [
      "Risques biologiques (infections, AES)",
      "Risques chimiques (produits d'entretien, désinfectants)",
      "Risques physiques (manutentions, chutes, bruit)",
      "Risques psychosociaux (stress, épuisement, violences)",
    ]},
  ]},

  // --- 6.2 Risques biologiques, chimiques, physiques, psychosociaux ---
  { type: 'subsection', title: "6.2 Types de risques" },
  { type: 'richtext', title: "Risques biologiques", blocks: [
    { text: "Les risques biologiques sont liés à l'exposition aux micro-organismes (bactéries, virus, champignons) :", list: [
      "Contact avec le sang et les liquides biologiques",
      "AES (Accident d'Exposition au Sang) : piqûre, coupure, projection",
      "Infections nosocomiales : infections contractées lors des soins",
    ]},
    { note: "Conduite à tenir en cas d'AES : nettoyer immédiatement la plaie, désinfecter, déclarer l'accident, consulter un médecin dans les 4 heures." },
  ]},
  { type: 'richtext', title: "Risques chimiques et physiques", blocks: [
    { text: "Risques chimiques :", list: [
      "Produits d'entretien : irritation de la peau, des voies respiratoires",
      "Désinfectants : brûlures, allergies",
      "Prévention : porter des gants, lire les étiquettes, respecter les dilutions",
    ]},
    { text: "Risques physiques :", list: [
      "Manutentions répétées (port de charges, mobilisations)",
      "Postures contraignantes et gestes répétitifs",
      "Chutes (sols mouillés, encombrements)",
    ]},
  ]},
  { type: 'richtext', title: "Risques psychosociaux", blocks: [
    { text: "Les risques psychosociaux (RPS) affectent la santé mentale et physique du professionnel :", list: [
      "Stress lié à la charge de travail, aux responsabilités",
      "Confrontation à la souffrance, à la mort, à l'agressivité",
      "Manque de reconnaissance ou de soutien de l'équipe",
      "Conflits au sein de l'équipe",
    ]},
  ]},

  // --- 6.3 Prévention des TMS ---
  { type: 'subsection', title: "6.3 Prévention des TMS" },
  { type: 'richtext', title: "TMS et PRAP", blocks: [
    { text: "Les TMS (Troubles Musculo-Squelettiques) sont la première cause de maladie professionnelle dans le secteur du soin.", list: [
      "Zones touchées : dos (lombalgie), épaules, poignets, genoux",
      "PRAP 2S : Prévention des Risques liés à l'Activité Physique – secteur sanitaire et social",
    ]},
    { text: "Principes de manutention :", list: [
      "Pieds écartés, dos droit, genoux fléchis, charge près du corps",
      "Utiliser les aides techniques : lève-personne, guidon de transfert, drap de glisse",
      "Travailler en binôme quand la situation l'exige",
    ]},
  ]},

  // --- 6.4 Prévention de l'épuisement professionnel ---
  { type: 'subsection', title: "6.4 Prévention de l'épuisement professionnel" },
  { type: 'richtext', title: "Le burn-out", blocks: [
    { text: "L'épuisement professionnel (burn-out) est un état de fatigue physique et émotionnelle intense, lié à un stress chronique au travail." },
    { text: "Signes d'alerte :", list: [
      "Fatigue permanente, insomnie",
      "Perte de motivation, cynisme",
      "Irritabilité, repli sur soi",
      "Difficultés de concentration, erreurs inhabituelles",
      "Absentéisme fréquent",
    ]},
    { note: "Le professionnel doit savoir demander de l'aide. Des dispositifs existent : psychologue du travail, médecin du travail, groupes de parole, entretien avec le cadre." },
  ]},

  // --- 6.5 Équipements de protection ---
  { type: 'subsection', title: "6.5 Équipements de protection et bonnes pratiques" },
  { type: 'richtext', title: "EPI et précautions standard", blocks: [
    { text: "Les équipements de protection individuelle (EPI) sont indispensables :", list: [
      "Gants à usage unique : pour tout contact avec le sang ou les liquides biologiques",
      "Tablier ou surblouse : pour les soins à risque de projection",
      "Masque : en cas de risque respiratoire ou de maladie transmissible",
      "Lunettes de protection : en cas de risque de projection oculaire",
    ]},
    { text: "Hygiène des mains : le geste de prévention le plus efficace. Friction hydro-alcoolique (SHA) ou lavage au savon doux." },
  ]},

  // --- 6.6 DUER ---
  { type: 'subsection', title: "6.6 DUER et démarche de prévention" },
  { type: 'richtext', title: "Le DUER", blocks: [
    { text: "Le DUER (Document Unique d'Évaluation des Risques) est un document obligatoire dans chaque établissement. Il recense l'ensemble des risques professionnels identifiés et les mesures de prévention mises en place." },
    { text: "Il est :", list: [
      "Mis à jour au moins une fois par an",
      "Accessible à tous les professionnels",
      "Élaboré en concertation avec les représentants du personnel",
    ]},
  ]},

  // =============================================
  // CHAPITRE 7 : Accueil, tutorat et accompagnement
  // =============================================
  { type: 'section', section: "7", title: "Accueil, tutorat et accompagnement" },

  // --- 7.1 Accueil ---
  { type: 'subsection', title: "7.1 Accueil d'un stagiaire ou nouveau collègue" },
  { type: 'richtext', title: "L'accueil", blocks: [
    { text: "Le professionnel peut être amené à accueillir un stagiaire, un nouvel arrivant ou un bénévole. Cet accueil contribue à la qualité du fonctionnement d'équipe et à la sécurité des pratiques." },
    { text: "Il suppose de présenter :", list: [
      "Les locaux et l'environnement de travail",
      "Les règles de fonctionnement du service",
      "L'organisation du travail et les protocoles",
      "L'équipe et les différents rôles",
      "Les repères essentiels : sécurité, confidentialité, outils",
    ]},
  ]},

  // --- 7.2 Transmission des savoirs ---
  { type: 'subsection', title: "7.2 Rôle dans la transmission des savoirs" },
  { type: 'richtext', title: "Transmettre ses savoirs", blocks: [
    { text: "Le professionnel participe à la transmission des savoirs du métier. Il montre, explique, verbalise les gestes et aide l'autre à comprendre les logiques d'intervention." },
    { text: "Cette transmission doit être :", list: [
      "Progressive : du simple au complexe",
      "Adaptée : au niveau et au rythme de l'apprenant",
      "Sécurisée : toujours sous surveillance dans un premier temps",
    ]},
    { text: "Le professionnel transmet également les valeurs professionnelles : éthique, respect, rigueur, bienveillance." },
  ]},

  // --- 7.3 Accompagnement dans l'intégration ---
  { type: 'subsection', title: "7.3 Accompagnement dans l'intégration" },
  { type: 'richtext', title: "L'intégration à l'équipe", blocks: [
    { text: "L'intégration ne se limite pas à la découverte des lieux. Elle suppose aussi l'entrée dans :", list: [
      "Une culture de service et des valeurs partagées",
      "Des habitudes de travail propres à l'équipe",
      "Un vocabulaire professionnel spécifique",
      "Une dynamique d'équipe avec ses codes et ses repères",
    ]},
    { text: "Le professionnel peut faciliter cette intégration par son attitude, sa disponibilité et sa bienveillance." },
  ]},
  { type: 'richtext', title: "Étapes de l'accompagnement", blocks: [
    { text: "L'intégration suit une progression :", list: [
      "Accueil (J1) : présentation, visite, planification, objectifs",
      "Observation (Semaine 1) : le stagiaire observe, pose des questions, prend ses repères",
      "Participation guidée : réalisation de tâches simples sous supervision directe",
      "Autonomie progressive : le stagiaire gagne en autonomie, le tuteur reste disponible",
      "Évaluation finale : bilan des acquis, points forts, axes d'amélioration",
    ]},
  ]},

  // --- 7.4 Observation, conseils et posture éducative ---
  { type: 'subsection', title: "7.4 Observation, conseils et posture éducative" },
  { type: 'richtext', title: "Posture éducative", blocks: [
    { text: "Dans une posture de tutorat, le professionnel observe la manière de faire du stagiaire ou du nouvel arrivant, donne des conseils, corrige si besoin et valorise les progrès." },
    { text: "Il adopte une posture éducative, exigeante mais bienveillante :", list: [
      "Observer avant d'intervenir",
      "Expliquer le sens des gestes et des décisions",
      "Laisser le droit à l'erreur tout en sécurisant",
      "Donner des retours constructifs",
      "Adapter son accompagnement au niveau et au rythme de l'apprenant",
    ]},
  ]},

  // --- 7.5 Limites du rôle de tutorat ---
  { type: 'subsection', title: "7.5 Limites du rôle de tutorat" },
  { type: 'richtext', title: "Limites du tutorat", blocks: [
    { text: "Le professionnel accompagne dans les limites de son rôle. Il ne se substitue pas au formateur, au cadre ou au responsable de stage." },
    { text: "Il transmet dans le cadre de ses missions et de ses compétences :", list: [
      "Il ne peut pas évaluer des compétences relevant d'un autre diplôme",
      "Il ne peut pas confier au stagiaire des actes qu'il n'est pas autorisé à réaliser",
    ]},
    { note: "Le tutorat est une mission valorisante. Si vous avez encadré des stagiaires ou de nouveaux collègues, c'est un point fort pour votre Livret 2 VAE." },
  ]},

  // =============================================
  // CHAPITRE 8 : Attendus professionnels et VAE
  // =============================================
  { type: 'section', section: "8", title: "Attendus professionnels et VAE" },

  { type: 'richtext', title: "Ce que le jury attend pour le Bloc 3", blocks: [
    { text: "Dans le cadre de la VAE, le jury attend du candidat qu'il soit capable de montrer qu'il travaille réellement en équipe et qu'il comprend sa place dans le collectif." },
    { text: "Il ne suffit pas de dire que l'on transmet des informations. Il faut expliquer :", list: [
      "Ce que l'on transmet",
      "À qui on le transmet",
      "Pourquoi on le transmet",
      "Avec quel support (oral, écrit, logiciel)",
      "Dans quel objectif (continuité, sécurité, coordination)",
    ]},
  ]},
  { type: 'richtext', title: "Démontrer sa participation", blocks: [
    { text: "Le candidat doit également démontrer qu'il :", list: [
      "Participe à la qualité du service",
      "Respecte la confidentialité et le secret professionnel",
      "Repère les risques et les signale",
      "Sait s'inscrire dans une organisation professionnelle",
    ]},
    { text: "Il doit montrer qu'il comprend la portée de ses transmissions et l'importance de la coordination." },
    { note: "Montrez que vous savez transmettre des informations pertinentes et que vous participez à la démarche qualité. Utilisez la méthode DAR dans vos exemples. Si vous avez encadré des stagiaires, décrivez votre démarche d'accompagnement." },
  ]},

  // ===== CONCLUSION =====
  { type: 'richtext', title: "Conclusion du Bloc 3", blocks: [
    { text: "Le travail en équipe pluriprofessionnelle est un pilier essentiel de l'accompagnement dans le secteur sanitaire et social. Il repose sur la communication, la coordination, la connaissance de sa place, le respect des rôles et la qualité des transmissions." },
    { text: "Le professionnel du Bac Pro ASSP contribue pleinement à cette dynamique collective. Par sa proximité avec la personne accompagnée, il est un acteur central de l'observation, de la transmission et de la continuité de l'accompagnement." },
  ]},

  // ===== FÉLICITATIONS =====
  { type: 'congratulations', title: "Félicitations !" },
];

const BACASSPBloc3Approche = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewedSlides, setViewedSlides] = useState(new Set([0]));
  const audioRef = useRef(null);
  const API_URL = window.location.origin || '';

  useEffect(() => {
    const saved = getActivityProgress(ACTIVITY_ID);
    if (saved?.viewedSlides) setViewedSlides(new Set(saved.viewedSlides));
  }, []);

  const speakText = async (text) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/tts/generate`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: text.substring(0, 800), voice: 'nova', speed: 1.1 }) });
      if (r.ok) { const d = await r.json(); if (d.audio_url || d.audio_base64) { if (audioRef.current) audioRef.current.pause(); audioRef.current = new Audio(d.audio_url || `data:audio/mp3;base64,${d.audio_base64}`); audioRef.current.play(); setIsPlaying(true); audioRef.current.onended = () => setIsPlaying(false); } }
    } catch (e) { console.log('TTS unavailable'); }
    setIsLoading(false);
  };
  const stopAudio = () => { if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; setIsPlaying(false); } };
  const getSlideText = (s) => { let t = s.title || ''; if (s.blocks) s.blocks.forEach(b => { if (b.heading) t += ' ' + b.heading; if (b.text) t += ' ' + b.text; if (b.list) b.list.forEach(li => t += ' ' + li); }); return t; };

  const nextSlide = () => { if (currentSlide < slides.length - 1) { const next = currentSlide + 1; setCurrentSlide(next); setViewedSlides(prev => new Set([...prev, next])); markSlideViewed(ACTIVITY_ID, next, slides.length); } };
  const prevSlide = () => { if (currentSlide > 0) setCurrentSlide(currentSlide - 1); };
  const goToSlide = (i) => { setCurrentSlide(i); setViewedSlides(prev => new Set([...prev, i])); markSlideViewed(ACTIVITY_ID, i, slides.length); };

  const progressPercent = Math.round((viewedSlides.size / slides.length) * 100);

  const renderSlide = (slide) => {
    switch (slide.type) {
      case 'title':
        return (
          <div className="flex items-center justify-center h-full relative" style={{ background: theme.bg }}>
            <img src={slide.image} alt="Bloc 3" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
            {slide.blocTitle && (
              <div className="absolute bottom-0 left-0 right-0 text-center" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.7))', padding: '2rem 1rem 1.5rem' }}>
                <h1 className="text-white font-bold text-lg md:text-xl drop-shadow-lg">{slide.blocTitle}</h1>
              </div>
            )}
          </div>
        );
      case 'sommaire':
        return (
          <div className="h-full flex flex-col relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(255,251,235,0.82)' }} />
            <div className="relative z-10 h-full flex flex-col">
              <div style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, #92400E 100%)`, padding: '0.6rem 0' }}>
                <h2 className="text-xl font-bold text-center text-white tracking-widest uppercase">Sommaire</h2>
              </div>
              <div className="flex-1 overflow-auto px-4 flex items-center justify-center">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl mx-auto">
                  {slide.sections.map((s, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-white/90 rounded-lg shadow-sm cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all backdrop-blur-sm" style={{ border: '1px solid #e0e0e0' }} onClick={() => goToSlide(s.target - 1)}>
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 text-white" style={{ background: theme.primary }}>{s.num}</span>
                      <span className="text-gray-700 text-sm leading-tight font-medium">{s.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      case 'section':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(217,119,6,0.45)' }} />
            <div className="text-center px-10 relative z-10">
              <span className="text-white/80 text-2xl md:text-3xl font-bold mb-3 block tracking-wide">Chapitre {slide.section}</span>
              <h2 className="text-xl md:text-2xl font-semibold text-white drop-shadow-lg">{slide.title}</h2>
            </div>
          </div>
        );
      case 'subsection':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(217,119,6,0.35)' }} />
            <div className="text-center px-10 relative z-10">
              <h2 className="text-2xl md:text-3xl font-bold text-white drop-shadow-lg">{slide.title}</h2>
            </div>
          </div>
        );
      case 'richtext':
        return (
          <div className="h-full overflow-auto flex items-center justify-center p-6" style={{ background: theme.bg }}>
            <div className="max-w-3xl w-full">
              <h2 className="text-lg font-bold mb-4 text-center" style={{ color: theme.primary }}>{slide.title}</h2>
              <div className="space-y-3">
                {slide.blocks.map((block, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 shadow-sm" style={{ border: `1px solid ${theme.primary}12` }}>
                    {block.heading && <h4 className="font-semibold mb-2 text-[15px]" style={{ color: theme.primary }}>{block.heading}</h4>}
                    {block.text && <p className="text-gray-700 text-[15px] leading-relaxed mb-2">{block.text}</p>}
                    {block.list && (
                      <div className="space-y-1.5">
                        {block.list.map((li, j) => (
                          <div key={j} className="flex items-start gap-2.5">
                            <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ background: theme.primary }} />
                            <span className="text-gray-600 text-[15px] leading-relaxed">{li}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {block.note && <p className="text-[14px] italic mt-2.5 p-2.5 rounded-md" style={{ background: `${theme.primary}08`, borderLeft: `3px solid ${theme.primary}`, color: '#475569' }}>{block.note}</p>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case 'congratulations':
        return (
          <div className="flex flex-col items-center justify-center h-full relative overflow-hidden" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="absolute inset-0" style={{ background: 'rgba(217,119,6,0.5)' }} />
            <div className="relative z-10 flex flex-col items-center text-center px-8 max-w-2xl">
              <div className="text-5xl mb-4">&#127942;</div>
              <h2 className="text-3xl font-bold text-white mb-4 drop-shadow-lg">{slide.title}</h2>
              <p className="text-white text-lg mb-3 leading-relaxed drop-shadow">Vous avez terminé la présentation du <strong>Bloc 3</strong> du BAC ASSP.</p>
              <p className="text-white text-base mb-6 leading-relaxed drop-shadow">Vous maîtrisez désormais les fondamentaux du travail en équipe et de la communication professionnelle.</p>
              <div className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.9)' }}>
                <p className="text-base font-semibold mb-1" style={{ color: theme.primary }}>Prêt(e) à tester vos connaissances ?</p>
                <p className="text-sm text-gray-600">Rendez-vous dans le <strong>Quiz - Bloc 3</strong> pour évaluer votre maîtrise.</p>
              </div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  const slide = slides[currentSlide];
  return (
    <div className="rounded-xl overflow-hidden shadow-lg" style={{ background: theme.bg }} data-testid="bac-assp-bloc3-presentation">
      <div style={{ minHeight: '480px', height: '480px' }}>{renderSlide(slide)}</div>
      <div className="w-full h-2 bg-gray-200">
        <div className="h-full rounded-r-full transition-all duration-300" style={{ width: `${progressPercent}%`, background: `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})` }} />
      </div>
      <div className="flex items-center justify-between p-3 bg-white border-t" style={{ borderColor: theme.primary + '30' }}>
        <button onClick={prevSlide} disabled={currentSlide === 0} className="flex items-center gap-1 px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-40" style={{ borderColor: theme.primary, color: theme.text }} data-testid="bloc3-prev-btn">
          <ChevronLeft size={16} /> Précédent
        </button>
        <div className="flex items-center gap-2">
          <button onClick={() => isPlaying ? stopAudio() : speakText(getSlideText(slide))} className="w-9 h-9 rounded-full flex items-center justify-center text-white" style={{ background: theme.primary }} data-testid="bloc3-audio-btn">
            {isLoading ? <Loader2 size={16} className="animate-spin" /> : isPlaying ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <span className="font-bold text-sm" style={{ color: theme.primary }}>{currentSlide + 1} / {slides.length}</span>
          <span className="text-xs text-gray-400">{progressPercent}%</span>
        </div>
        <button onClick={nextSlide} disabled={currentSlide === slides.length - 1} className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-40" style={{ background: theme.primary }} data-testid="bloc3-next-btn">
          Suivant <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default BACASSPBloc3Approche;
